import {
  MFA_CODE_SENT,
  MFA_CUSTOMER_INFO,
  MFA_LOGIN_CHALLENGE,
  MFA_RECOVERY_ID,
  MFA_SESSION_EXPIRED_NOTICE,
  MFA_TOKEN_MISSING_REDIRECTED,
} from "constants/mfaAndOtp";
import { getLoginLink } from "utils/urls";

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * recovery_id previously returned by send-mfa (see redirectToLoginForMissingMfaToken),
 * or "" if none / storage blocked.
 */
export const getMfaRecoveryId = () => {
  try {
    const value = sessionStorage.getItem(MFA_RECOVERY_ID);
    return value && UUID_PATTERN.test(value) ? value : "";
  } catch (e) {
    return "";
  }
};

/**
 * Called when id-account-auth reports that the MFA-TOKEN cookie is missing
 * (expired after 1h, already cleared, killed webview, etc.).
 *
 * recoveryId (from the send-mfa response) is kept in sessionStorage so the
 * login form can post it back; id-account-auth then logs whether this
 * customer's retry succeeded. It is not added to the login page URL.
 *
 * First time in this tab: clear MFA state and send the customer back to login
 * (keeping the login_challenge) so a fresh cookie is issued. Returns true.
 *
 * If it happens again after that fresh login, the browser is not storing the
 * cookie at all, so redirecting again would just loop the customer between
 * login and OTP. In that case (or if sessionStorage itself is blocked) we do
 * NOT redirect and return false so the caller can show an error message.
 */
export const redirectToLoginForMissingMfaToken = (recoveryId) => {
  try {
    if (recoveryId && UUID_PATTERN.test(recoveryId)) {
      sessionStorage.setItem(MFA_RECOVERY_ID, recoveryId);
    }
    if (sessionStorage.getItem(MFA_TOKEN_MISSING_REDIRECTED) === "true") {
      return false;
    }
    sessionStorage.setItem(MFA_TOKEN_MISSING_REDIRECTED, "true");
    sessionStorage.setItem(MFA_SESSION_EXPIRED_NOTICE, "true");
    sessionStorage.removeItem(MFA_CODE_SENT);
    sessionStorage.removeItem(MFA_CUSTOMER_INFO);
    sessionStorage.removeItem(MFA_LOGIN_CHALLENGE);
  } catch (e) {
    // Storage blocked (typically together with cookies) - we cannot guard
    // against a loop, so do not redirect.
    return false;
  }

  const loginChallenge = new URLSearchParams(window.location.search).get(
    "login_challenge"
  );
  const search = loginChallenge
    ? `?login_challenge=${encodeURIComponent(loginChallenge)}`
    : "";
  window.location.assign(
    `${process.env.PUBLIC_URL || ""}${getLoginLink()}${search}`
  );
  return true;
};

/** True if the customer was just redirected to login because the MFA-TOKEN cookie was missing. */
export const hasMfaSessionExpiredNotice = () => {
  try {
    return sessionStorage.getItem(MFA_SESSION_EXPIRED_NOTICE) === "true";
  } catch (e) {
    return false;
  }
};

/** The notice is one-shot: clear it once the login page has read it. */
export const clearMfaSessionExpiredNotice = () => {
  try {
    sessionStorage.removeItem(MFA_SESSION_EXPIRED_NOTICE);
  } catch (e) {
    // ignore - storage unavailable
  }
};

/**
 * Call after a successful send-mfa: the cookie is working, so reset the guard.
 * The recovery id is already inside the new MFA-TOKEN cookie, so it is removed
 * here to stop it being attached to later, unrelated logins in this tab.
 */
export const resetMissingMfaTokenGuard = () => {
  try {
    sessionStorage.removeItem(MFA_TOKEN_MISSING_REDIRECTED);
    sessionStorage.removeItem(MFA_RECOVERY_ID);
  } catch (e) {
    // ignore - storage unavailable
  }
};
